// ---------------------------------------------------------------------------
// aggregateColumnData -- Data Aggregation Utility
// ---------------------------------------------------------------------------
// Transforms raw data rows into chart-ready format:
//   - Categorical (bar/donut): counts occurrences of each unique value
//   - Numeric (gauge): computes average, min, max
// ---------------------------------------------------------------------------

import type { ChartType } from '@/components/v11/discovery/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AggregatedItem {
  readonly label: string
  readonly value: number
}

export interface AggregatedResult {
  readonly type: 'categorical' | 'numeric'
  readonly items: ReadonlyArray<AggregatedItem>
  readonly average: number
  readonly min: number
  readonly max: number
  readonly total: number
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function aggregateColumnData(
  data: ReadonlyArray<Record<string, unknown>>,
  columnKey: string,
  chartType: ChartType,
  maxItems?: number,
): AggregatedResult {
  const emptyResult: AggregatedResult = {
    type: chartType === 'gauge' ? 'numeric' : 'categorical',
    items: [],
    average: 0,
    min: 0,
    max: 0,
    total: 0,
  }

  if (data.length === 0) return emptyResult

  // Extract column values, filtering out null/undefined
  const rawValues = data
    .map((row) => row[columnKey])
    .filter((v): v is NonNullable<typeof v> => v !== null && v !== undefined)

  if (rawValues.length === 0) return emptyResult

  // Numeric aggregation (gauge)
  if (chartType === 'gauge') {
    const numbers = rawValues
      .map((v) => (typeof v === 'number' ? v : parseFloat(String(v))))
      .filter((n) => !isNaN(n))

    if (numbers.length === 0) return emptyResult

    const sum = numbers.reduce((acc, n) => acc + n, 0)
    const avg = sum / numbers.length
    const min = Math.min(...numbers)
    const max = Math.max(...numbers)

    return {
      type: 'numeric',
      items: [],
      average: avg,
      min,
      max,
      total: numbers.length,
    }
  }

  // Categorical aggregation (bar/donut)
  const counts = new Map<string, number>()
  for (const val of rawValues) {
    const key = String(val)
    counts.set(key, (counts.get(key) || 0) + 1)
  }

  // Sort by count descending
  const sorted = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => ({ label, value }))

  const items = maxItems ? sorted.slice(0, maxItems) : sorted
  const total = items.reduce((sum, item) => sum + item.value, 0)

  return {
    type: 'categorical',
    items,
    average: 0,
    min: 0,
    max: 0,
    total,
  }
}
